interface CellFormat {
  numFmt?: string;
}

interface SpreadsheetCell {
  value: string | number | null;
  className?: string;
}

export function formatCellValue(value: any, format: CellFormat | undefined): SpreadsheetCell {
  if (value === null || value === undefined) {
    return { value: '' };
  }

  // Handle accounting format with three sections
  if (typeof value === 'number' && format?.numFmt) {
    const formatParts = format.numFmt.split(';');
    const positiveFormat = formatParts[0] || '';
    const negativeFormat = formatParts[1] || '';
    const zeroFormat = formatParts[2] || '';

    // Check for accounting format pattern
    const isAccounting = positiveFormat.includes('_') || 
                       negativeFormat.includes('(') ||
                       zeroFormat === '"-"';

    if (isAccounting) {
      if (value === 0 && zeroFormat === '"-"') {
        return {
          value: '-',
          className: 'text-right font-mono text-gray-500'
        };
      }

      const formattedNumber = Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true
      });

      if (value < 0) {
        return {
          value: `(${formattedNumber})`,
          className: 'text-right font-mono text-red-600'
        };
      }

      // Add right padding for positive numbers (emulate Excel's _) character)
      return {
        value: `${formattedNumber} `,
        className: 'text-right font-mono text-gray-900'
      };
    }

    // Handle currency formatting
    if (format.numFmt.includes('$') || 
        format.numFmt.includes('¥') || 
        format.numFmt.includes('€') ||
        format.numFmt.includes('[$$-409]')) {
      return {
        value: new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value),
        className: `text-right font-mono ${value < 0 ? 'text-red-600' : 'text-emerald-700'}`
      };
    }

    // Handle percentage formatting
    if (format.numFmt.includes('%')) {
      return {
        value: `${(value * 100).toFixed(2)}%`,
        className: 'text-right font-mono'
      };
    }

    // Handle number formatting with specific decimal places
    const decimalPlaces = format.numFmt.match(/\.0+/)?.[0].length - 1 || 0;
    return {
      value: value.toFixed(decimalPlaces),
      className: 'text-right font-mono'
    };
  }

  // Handle dates
  if (value instanceof Date) {
    return {
      value: value.toLocaleDateString(),
      className: 'text-center'
    };
  }

  // Default formatting
  return {
    value: value.toString(),
    className: typeof value === 'number' ? 'text-right font-mono' : ''
  };
}